import React, { useEffect, useState, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import {
    Layout, Save, Smartphone, Type, Link, Timer,
    Image as ImageIcon, Plus, Edit, Trash2,
    ChevronRight, Upload, X, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export const MobileBannersSection = () => {
    const [banners, setBanners] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [editingBanner, setEditingBanner] = useState<any>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState({
        mini_titulo: '',
        titulo: '',
        texto: '',
        button_personalized: '',
        visable_button: true,
        link_button: '',
        banner: '',
        banner_time: 8000,
        Screen_formulario: false
    });

    const { toast } = useToast();

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from('banners_mobile')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setBanners(data || []);
        } catch (e) {
            console.error("Erro ao buscar banners mobile:", e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast({
                title: "Arquivo inválido",
                description: "Por favor, selecione uma imagem.",
                variant: "destructive"
            });
            return;
        }

        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
            const filePath = `banners/${fileName}`;

            const { data, error } = await supabase.storage
                .from('banner_mobile')
                .upload(filePath, file);

            if (error) throw error;

            const { data: { publicUrl } } = supabase.storage
                .from('banner_mobile')
                .getPublicUrl(filePath);

            setFormData(prev => ({ ...prev, banner: publicUrl }));

            toast({
                title: "Upload concluído",
                description: "Imagem enviada com sucesso para o storage."
            });
        } catch (error: any) {
            console.error("Erro no upload:", error);
            toast({
                title: "Erro no upload",
                description: error.message || "Não foi possível enviar a imagem.",
                variant: "destructive"
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleOpenModal = (banner: any = null) => {
        if (banner) {
            setEditingBanner(banner);
            setFormData({
                mini_titulo: banner.mini_titulo || '',
                titulo: banner.titulo || '',
                texto: banner.texto || '',
                button_personalized: banner.button_personalized || '',
                visable_button: banner.visable_button !== false,
                link_button: banner.link_button || '',
                banner: banner.banner || '',
                banner_time: banner.banner_time || 8000,
                Screen_formulario: !!banner.Screen_formulario
            });
        } else {
            setEditingBanner(null);
            setFormData({
                mini_titulo: '',
                titulo: '',
                texto: '',
                button_personalized: '',
                visable_button: true,
                link_button: '',
                banner: '',
                banner_time: 8000,
                Screen_formulario: false
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const payload = {
                mini_titulo: formData.mini_titulo,
                titulo: formData.titulo,
                texto: formData.texto,
                button_personalized: formData.button_personalized,
                visable_button: formData.visable_button,
                link_button: formData.link_button,
                banner: formData.banner,
                banner_time: formData.banner_time,
                Screen_formulario: formData.Screen_formulario,
            };

            let error;
            if (editingBanner) {
                const result = await supabase
                    .from('banners_mobile')
                    .update(payload)
                    .eq('id', editingBanner.id);
                error = result.error;
            } else {
                const result = await supabase
                    .from('banners_mobile')
                    .insert(payload);
                error = result.error;
            }

            if (error) throw error;

            toast({
                title: editingBanner ? "Banner atualizado" : "Banner criado",
                description: `O banner foi ${editingBanner ? 'atualizado' : 'cadastrado'} com sucesso.`
            });
            setIsModalOpen(false);
            fetchBanners();
        } catch (error) {
            console.error("Erro ao salvar banner mobile:", error);
            toast({
                title: "Erro ao salvar",
                description: "Não foi possível processar a solicitação.",
                variant: 'destructive',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Tem certeza que deseja excluir este banner?")) return;

        try {
            const { error } = await supabase
                .from('banners_mobile')
                .delete()
                .eq('id', id);

            if (error) throw error;

            toast({
                title: "Banner excluído",
                description: "O banner foi removido permanentemente."
            });
            fetchBanners();
        } catch (error) {
            console.error("Erro ao excluir banner:", error);
            toast({
                title: "Erro ao excluir",
                description: "Ocorreu um problema ao tentar remover o banner.",
                variant: 'destructive'
            });
        }
    };

    if (isLoading && banners.length === 0) {
        return (
            <div className="p-12 text-center">
                <div className="w-8 h-8 border-3 border-emerald-100 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-slate-500 font-medium font-outfit">Carregando banners mobile...</p>
            </div>
        );
    }

    return (
        <Card className="w-full bg-slate-50/50 border-none shadow-none">
            <CardHeader className="px-0 pt-0 pb-6 flex flex-row items-center justify-between">
                <div className="flex items-center gap-4 font-outfit">
                    <div className="p-3.5 bg-emerald-600 rounded-2xl text-white shadow-lg shadow-emerald-200/50">
                        <Smartphone className="w-6 h-6" />
                    </div>
                    <div>
                        <CardTitle className="text-2xl text-slate-800 font-black tracking-tight">Banners Mobile (Copilot)</CardTitle>
                        <CardDescription className="text-slate-500 font-medium font-outfit">
                            Gerencie múltiplos banners para o aplicativo móvel.
                        </CardDescription>
                    </div>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl px-6 h-12 shadow-md shadow-emerald-100 flex items-center gap-2 group transition-all"
                >
                    <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                    Novo Banner
                </Button>
            </CardHeader>

            <CardContent className="px-0 font-outfit">
                {banners.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
                        <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ImageIcon className="w-8 h-8 text-slate-300" />
                        </div>
                        <h3 className="text-slate-800 font-bold mb-1">Nenhum banner encontrado</h3>
                        <p className="text-slate-500 text-sm mb-6">Comece criando seu primeiro banner para o app mobile.</p>
                        <Button variant="outline" onClick={() => handleOpenModal()} className="border-slate-200 text-slate-600">
                            Criar Primeiro Banner
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3">
                        {banners.map((banner) => (
                            <div
                                key={banner.id}
                                className="group bg-white rounded-2xl border border-slate-100 p-4 hover:border-emerald-200 hover:shadow-md hover:shadow-emerald-500/5 transition-all duration-300 flex items-center gap-4"
                            >
                                <div className="w-32 h-20 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0 relative border border-slate-50">
                                    {banner.banner ? (
                                        <img src={banner.banner} alt={banner.titulo} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                            <ImageIcon size={24} />
                                        </div>
                                    )}
                                    <div className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-tighter ${banner.visable_button ? 'bg-emerald-500 text-white' : 'bg-slate-400 text-white'}`}>
                                        {banner.visable_button ? 'Botão Ativo' : 'Sem Botão'}
                                    </div>
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <h4 className="font-bold text-slate-900 truncate">{banner.titulo || 'Sem título'}</h4>
                                        {banner.Screen_formulario && (
                                            <span className="bg-amber-50 text-amber-600 text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase border border-amber-100">Lead Gen</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 truncate max-w-md italic">{banner.mini_titulo}</p>
                                    <div className="flex items-center gap-3 mt-2">
                                        <div className="flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                                            <Timer size={12} />
                                            {banner.banner_time}ms
                                        </div>
                                        {banner.link_button && (
                                            <div className="flex items-center gap-1 text-[10px] text-emerald-600 font-bold">
                                                <Link size={12} />
                                                Link Configurado
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleOpenModal(banner)}
                                        className="h-9 w-9 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl"
                                    >
                                        <Edit size={18} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDelete(banner.id)}
                                        className="h-9 w-9 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl"
                                    >
                                        <Trash2 size={18} />
                                    </Button>
                                    <div className="w-px h-6 bg-slate-100 mx-1" />
                                    <ChevronRight className="text-slate-300" size={20} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-3xl rounded-3xl p-0 overflow-hidden border-none shadow-2xl font-outfit">
                    <form onSubmit={handleSave}>
                        <div className="p-8 bg-white max-h-[85vh] overflow-y-auto custom-scrollbar">
                            <DialogHeader className="mb-8 font-outfit">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600">
                                        <Plus className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <DialogTitle className="text-2xl font-black text-slate-800 tracking-tight">
                                            {editingBanner ? 'Editar Banner' : 'Novo Banner Mobile'}
                                        </DialogTitle>
                                        <DialogDescription className="text-slate-500 font-medium">
                                            Envie uma imagem e preencha os dados do Watch Copilot.
                                        </DialogDescription>
                                    </div>
                                </div>
                            </DialogHeader>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-5">
                                    <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-widest mb-2">
                                        <Type size={14} /> Conteúdo Escrito
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Mini Título</Label>
                                        <Input
                                            value={formData.mini_titulo}
                                            onChange={(e) => setFormData(p => ({ ...p, mini_titulo: e.target.value }))}
                                            className="h-12 rounded-xl bg-slate-50/50 border-slate-100 focus:bg-white transition-all shadow-none"
                                            placeholder="Ex: NOVO NO ECOSSISTEMA?"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Título Principal</Label>
                                        <Input
                                            value={formData.titulo}
                                            onChange={(e) => setFormData(p => ({ ...p, titulo: e.target.value }))}
                                            className="h-12 rounded-xl bg-slate-50/50 border-slate-100 focus:bg-white transition-all shadow-none font-bold"
                                            placeholder="Ex: SEJA UM PARCEIRO"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Breve Descrição</Label>
                                        <textarea
                                            value={formData.texto}
                                            onChange={(e) => setFormData(p => ({ ...p, texto: e.target.value }))}
                                            className="w-full h-32 rounded-xl bg-slate-50/50 border border-slate-100 p-4 text-sm focus:bg-white transition-all outline-none focus:ring-2 focus:ring-emerald-500/10 focus:border-emerald-500/50"
                                            placeholder="Explique os benefícios deste banner..."
                                        />
                                    </div>
                                </div>

                                <div className="space-y-5">
                                    <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-widest mb-2">
                                        <Layout size={14} /> Mídia e Link
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Imagem do Banner</Label>

                                        <div className="flex flex-col gap-3">
                                            {formData.banner && (
                                                <div className="relative w-full h-32 rounded-2xl overflow-hidden border border-slate-100 group">
                                                    <img src={formData.banner} alt="Preview" className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData(p => ({ ...p, banner: '' }))}
                                                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            )}

                                            <div className="flex gap-2">
                                                <div className="relative flex-1 group">
                                                    <Input
                                                        value={formData.banner}
                                                        onChange={(e) => setFormData(p => ({ ...p, banner: e.target.value }))}
                                                        className="h-12 pl-12 rounded-xl bg-slate-50/50 border-slate-100 focus:bg-white transition-all shadow-none"
                                                        placeholder="URL ou UPLOAD →"
                                                    />
                                                    <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-hover:text-emerald-500 transition-colors" size={20} />
                                                </div>

                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={handleFileUpload}
                                                />

                                                <Button
                                                    type="button"
                                                    disabled={isUploading}
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="h-12 w-12 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex-shrink-0"
                                                >
                                                    {isUploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Botão</Label>
                                            <Input
                                                value={formData.button_personalized}
                                                onChange={(e) => setFormData(p => ({ ...p, button_personalized: e.target.value }))}
                                                className="h-12 rounded-xl bg-slate-50/50 border-slate-100"
                                                placeholder="Ex: SAIBA MAIS"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Tempo (ms)</Label>
                                            <Input
                                                type="number"
                                                value={formData.banner_time}
                                                onChange={(e) => setFormData(p => ({ ...p, banner_time: parseInt(e.target.value) }))}
                                                className="h-12 rounded-xl bg-slate-50/50 border-slate-100"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Link</Label>
                                        <div className="relative group">
                                            <Input
                                                value={formData.link_button}
                                                onChange={(e) => setFormData(p => ({ ...p, link_button: e.target.value }))}
                                                className="h-12 pl-12 rounded-xl bg-slate-50/50 border-slate-100 focus:bg-white transition-all shadow-none"
                                                placeholder="https://..."
                                            />
                                            <Link className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-hover:text-emerald-500 transition-colors" size={20} />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-2 pt-2">
                                        <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                                            <span className="text-xs font-bold text-slate-700">Botão</span>
                                            <Switch
                                                checked={formData.visable_button}
                                                onCheckedChange={(c) => setFormData(p => ({ ...p, visable_button: c }))}
                                                className="data-[state=checked]:bg-emerald-600"
                                            />
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                                            <span className="text-xs font-bold text-slate-700">Formulário</span>
                                            <Switch
                                                checked={formData.Screen_formulario}
                                                onCheckedChange={(c) => setFormData(p => ({ ...p, Screen_formulario: c }))}
                                                className="data-[state=checked]:bg-amber-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex justify-end gap-3 font-outfit">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setIsModalOpen(false)}
                                className="h-12 px-8 rounded-xl font-bold text-slate-500 hover:text-slate-700"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSaving || isUploading}
                                className="h-12 px-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black shadow-lg shadow-emerald-200/50 transition-all flex items-center gap-2"
                            >
                                {isSaving ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <Save size={18} />
                                        {editingBanner ? 'Salvar' : 'Criar'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </Card>
    );
};
